import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaEye,
  FaTimes,
  FaReceipt,
  FaCalendarAlt,
  FaSearch,
  FaSpinner,
  FaArrowUp,
  FaArrowDown,
  FaCoins,
  FaFileInvoiceDollar,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import apiRequest from "../services/api";

const etb = (n) => `${(Number(n) || 0).toFixed(2)}`;

const statusStyle = (status) => {
  const s = (status || "").toUpperCase();

  if (s === "POSTED" || s === "PAID" || s === "COMPLETED")
    return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20";

  if (s === "DRAFT" || s === "PENDING")
    return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20";

  if (s === "CANCELLED" || s === "VOID")
    return "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20";

  return "bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-white/5";
};

const typeLabel = (type) => {
  if (!type) return "—";
  return String(type).replace(/_/g, " ");
};

const todayStr = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const monthStartStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const PAGE_SIZE_OPTIONS = [15, 20, 50, 100];

const WithholdingLogs = ({ type }) => {
  const [logs, setLogs] = useState([]);
  const today = new Date();
  const currentYear = today.getFullYear();
  const [fromDate, setFromDate] = useState(`${currentYear}-01-01`);

  const [toDate, setToDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [direction, setDirection] = useState("ALL");
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Client-side pagination
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!fromDate || !toDate) {
      setError("Date range is required.");
      return;
    }

    if (fromDate > toDate) {
      setError("Invalid date range: From cannot be after To.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const endpoint = `reports/withholding/logs?type=${encodeURIComponent(
        type
      )}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(
        toDate
      )}&direction=${encodeURIComponent(direction)}`;

      const resp = await apiRequest(endpoint);

      const list = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp)
        ? resp
        : [];

      setLogs(list);
      setPage(0); // reset to first page after reload
    } catch (err) {
      setError(err?.message || "Failed to load withholding logs.");
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, direction, type]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Filtered list (client-side keyword filter)
  const filtered = useMemo(() => {
    if (!keyword.trim()) return logs;

    const q = keyword.trim().toLowerCase();

    return logs.filter((row) => {
      const hay = [
        row.invoiceNumber,
        row.receiptNumber,
        row.irn,
        row.rrn,
        row.note,
        row.type,
        row.direction,
        row.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [logs, keyword]);

  // Reset page when filter or page size changes
  useEffect(() => {
    setPage(0);
  }, [keyword, size]);

  // Pagination derived values
  const totalElements = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / size));
  const safePage = Math.min(page, totalPages - 1);

  const paginated = useMemo(() => {
    const start = safePage * size;
    return filtered.slice(start, start + size);
  }, [filtered, safePage, size]);

  const openView = (row) => {
    setSelected(row);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelected(null);
  };

  // Totals always over the full filtered set
  const totals = filtered.reduce(
    (acc, row) => {
      const amt = Number(row.amount) || 0;

      if ((row.direction || "").toUpperCase() === "RECEIVABLE") {
        acc.in += amt;
      } else {
        acc.out += amt;
      }

      acc.taxable += Number(row.taxableAmount) || 0;

      return acc;
    },
    { in: 0, out: 0, taxable: 0 }
  );

  const inputClass =
    "rounded-xl px-3 py-2 text-xs transition focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-slate-50 border border-slate-200 text-slate-800 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  return (
    <div className="min-h-full relative overflow-hidden font-sans p-4 md:p-8 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      {/* Background glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-emerald-500/5 dark:bg-emerald-500/10 blur-[120px]" />

      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-blue-500/5 dark:bg-blue-500/10 blur-[150px]" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-6">
        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 rounded-2xl border shadow-sm backdrop-blur-xl bg-white border-slate-200 dark:bg-slate-900/40 dark:border-white/10 dark:shadow-2xl">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
              <FaFileInvoiceDollar className="text-emerald-600 dark:text-emerald-400" />
              Withholding Logs
            </h1>

            <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
              Income tax &amp; VAT withholding entries by date range
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            {/* FROM DATE */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">
                From
              </label>

              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 dark:text-slate-500 pointer-events-none" />

                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`${inputClass} pl-8`}
                />
              </div>
            </div>

            {/* TO DATE */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">
                To
              </label>

              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 dark:text-slate-500 pointer-events-none" />

                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`${inputClass} pl-8`}
                />
              </div>
            </div>

            {/* DIRECTION */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-wider">
                Direction
              </label>

              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                className={inputClass}
              >
                <option value="ALL">All</option>
                <option value="RECEIVABLE">Receivable</option>
                <option value="PAYABLE">Payable</option>
              </select>
            </div>

            {/* LOAD */}
            <button
              type="button"
              onClick={fetchLogs}
              disabled={isLoading}
              className="flex items-center gap-2 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg disabled:opacity-50"
            >
              {isLoading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaSearch />
              )}
              Load
            </button>
          </div>
        </div>

        {/* SUMMARY */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
            <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
              Taxable base
            </p>

            <p className="mt-1 font-mono font-bold text-slate-800 dark:text-white text-sm">
              {etb(totals.taxable)} ETB
            </p>
          </div>

          <div className="p-5 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
            <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
              Withheld RECEIVABLE
            </p>

            <p className="mt-1 font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm">
              +{etb(totals.in)} ETB
            </p>
          </div>

          <div className="p-5 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
            <p className="text-[10px] uppercase font-bold tracking-wider text-slate-500 dark:text-slate-400">
              Withheld PAYABLE
            </p>

            <p className="mt-1 font-mono font-bold text-rose-600 dark:text-rose-400 text-sm">
              -{etb(totals.out)} ETB
            </p>
          </div>
        </div>

        {/* SEARCH */}
        <div className="relative max-w-md">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-slate-400 dark:text-slate-500 text-xs">
            <FaSearch />
          </span>

          <input
            type="text"
            placeholder="Filter by invoice, IRN, RRN, type, note..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="w-full bg-white border border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl pl-10 pr-4 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-900/50 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50 transition-all"
          />
        </div>

        {/* ERROR */}
        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-medium dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-400">
            {error}
          </div>
        )}

        {/* TABLE */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[960px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-400">
                  <th className="px-6 py-4">Invoice</th>
                  <th className="px-6 py-4">Type</th>
                  <th className="px-6 py-4">Direction</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4 text-right">Taxable</th>
                  <th className="px-6 py-4 text-right">Rate</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-600 text-xs dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-6 py-12 text-center text-slate-500 dark:text-slate-500"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <FaSpinner className="animate-spin text-emerald-500 text-base" />
                        <span>Loading logs...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-6 py-12 text-center text-slate-400 dark:text-slate-500 italic"
                    >
                      No withholding records in this range.
                    </td>
                  </tr>
                ) : (
                  paginated.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                    >
                      {/* INVOICE */}
                      <td className="px-6 py-4 font-mono font-semibold text-slate-800 dark:text-white tracking-wide">
                        <div className="text-slate-700 dark:text-slate-200 font-medium max-w-xs truncate">
                          {row.invoiceNumber || row.receiptNumber || "—"}
                        </div>

                        {(row.irn || row.rrn) && (
                          <div className="text-[10px] font-normal text-slate-400 dark:text-slate-500 flex items-start gap-1 mt-0.5 min-w-0">
                            <FaReceipt className="text-[9px] flex-shrink-0 mt-0.5" />

                            <span className="min-w-0 break-all">
                              {row.irn || row.rrn}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* TYPE */}
                      <td className="px-6 py-4">
                        <span className="text-slate-600 bg-slate-100 px-2 py-0.5 rounded text-[10px] border border-slate-200 font-medium dark:text-slate-400 dark:bg-slate-800 dark:border-white/5">
                          {typeLabel(row.type)}
                        </span>
                      </td>

                      {/* DIRECTION */}
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide inline-flex items-center gap-1 ${
                            (row.direction || "").toUpperCase() === "RECEIVABLE"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/10"
                              : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/10"
                          }`}
                        >
                          {(row.direction || "").toUpperCase() ===
                          "RECEIVABLE" ? (
                            <FaArrowDown className="text-[9px]" />
                          ) : (
                            <FaArrowUp className="text-[9px]" />
                          )}

                          {row.direction || "—"}
                        </span>
                      </td>

                      {/* STATUS */}
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider border uppercase ${statusStyle(
                            row.status
                          )}`}
                        >
                          {row.status || "—"}
                        </span>
                      </td>

                      {/* DATE */}
                      <td className="px-6 py-4 text-slate-500 dark:text-slate-400 font-mono text-[11px]">
                        {row.withholdingDate
                          ? new Date(row.withholdingDate).toLocaleString(
                              undefined,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )
                          : "—"}
                      </td>

                      {/* TAXABLE */}
                      <td className="px-6 py-4 text-right font-mono text-slate-600 dark:text-slate-300">
                        {etb(row.taxableAmount)}
                      </td>

                      {/* RATE */}
                      <td className="px-6 py-4 text-right font-mono text-slate-500 dark:text-slate-400">
                        {row.rate != null
                          ? `${Number(row.rate).toFixed(2)}%`
                          : "—"}
                      </td>

                      {/* AMOUNT */}
                      <td
                        className={`px-6 py-4 text-right font-mono font-bold text-sm ${
                          (row.direction || "").toUpperCase() === "RECEIVABLE"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-rose-600 dark:text-rose-400"
                        }`}
                      >
                        {(row.direction || "").toUpperCase() === "RECEIVABLE"
                          ? "+"
                          : "-"}
                        {etb(row.amount)}
                      </td>

                      {/* ACTION */}
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => openView(row)}
                          className="p-2 bg-slate-100 text-slate-500 border border-slate-200 rounded-lg hover:text-slate-800 hover:bg-slate-200 transition-colors dark:bg-slate-800 dark:border-white/5 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700"
                          title="View"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination + page-size selector */}
          {(totalPages > 1 || totalElements > 0) && !isLoading && (
            <div
              className="px-6 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs
              bg-slate-50 border-slate-200 text-slate-500
              dark:bg-slate-950/40 dark:border-white/5 dark:text-slate-400"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  Showing{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {paginated.length}
                  </span>{" "}
                  of{" "}
                  <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                    {totalElements}
                  </span>{" "}
                  records.
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-slate-500 dark:text-slate-400">
                    Rows per page:
                  </span>
                  <select
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold border appearance-none focus:outline-none focus:border-emerald-500/50
                      bg-white border-slate-200 text-slate-800
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                  >
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={safePage === 0}
                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
                      bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="font-mono text-slate-700 dark:text-slate-200">
                    Page {safePage + 1} of {totalPages}
                  </span>
                  <button
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setPage((prev) => prev + 1)}
                    className="p-2 rounded-md border transition disabled:opacity-30 disabled:pointer-events-none
                      bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100
                      dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* VIEW DRAWER – unchanged */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
          onClick={closeModal}
        />

        <div
          className={`relative w-full max-w-2xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto space-y-6 shadow-2xl transition-transform duration-300 transform dark:bg-slate-900 dark:border-white/10 ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* DRAWER HEADER */}
          <div className="flex justify-between items-start pb-3 border-b border-slate-100 dark:border-white/5">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
              <FaCoins className="text-emerald-600 dark:text-emerald-500" />
              Withholding Detail
            </h3>

            <button
              type="button"
              onClick={closeModal}
              className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition dark:text-slate-300 dark:hover:text-white dark:hover:bg-white/5"
            >
              <FaTimes />
            </button>
          </div>

          {selected && (
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4 text-xs dark:bg-slate-950/50 dark:border-white/10">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Invoice
                  </p>
                  <p className="font-mono text-slate-800 dark:text-white text-sm">
                    {selected.invoiceNumber || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Receipt #
                  </p>
                  <p className="font-mono text-slate-800 dark:text-white text-sm">
                    {selected.receiptNumber || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    IRN
                  </p>
                  <p className="font-mono text-slate-600 dark:text-slate-300 break-all">
                    {selected.irn || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    RRN
                  </p>
                  <p className="font-mono text-slate-600 dark:text-slate-300 break-all">
                    {selected.rrn || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Type
                  </p>
                  <p className="text-slate-600 dark:text-slate-300">
                    {typeLabel(selected.type)}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Direction
                  </p>
                  <p className="font-medium text-slate-600 dark:text-slate-300">
                    {selected.direction || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Status
                  </p>
                  <p className="text-slate-600 dark:text-slate-300">
                    {selected.status || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Date
                  </p>
                  <p className="font-mono text-slate-600 dark:text-slate-300">
                    {selected.withholdingDate
                      ? new Date(selected.withholdingDate).toLocaleString()
                      : "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Taxable Amount
                  </p>
                  <p className="font-mono text-slate-800 dark:text-white text-sm">
                    {etb(selected.taxableAmount)} ETB
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Rate
                  </p>
                  <p className="font-mono text-slate-800 dark:text-white text-sm">
                    {selected.rate != null
                      ? `${Number(selected.rate).toFixed(2)}%`
                      : "—"}
                  </p>
                </div>

                <div>
                  <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                    Withheld Amount
                  </p>
                  <p
                    className={`font-mono text-sm font-bold ${
                      (selected.direction || "").toUpperCase() === "RECEIVABLE"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400"
                    }`}
                  >
                    {(selected.direction || "").toUpperCase() === "RECEIVABLE"
                      ? "+"
                      : "-"}
                    {etb(selected.amount)} ETB
                  </p>
                </div>

                {selected.note && (
                  <div className="col-span-2">
                    <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px]">
                      Note
                    </p>
                    <p className="text-slate-600 dark:text-slate-300 mt-0.5">
                      {selected.note}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-white/5">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithholdingLogs;
